import { Box, Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import type { ObservationRow } from "../types";
import { Sparkline } from "./Sparkline";

const Root = styled(Paper)(({ theme }) => ({ padding: theme.spacing(2, 2.5) }));

const Heading = styled(Typography)(({ theme }) => ({
  ...theme.typography.h2,
  marginBottom: theme.spacing(1.5),
}));

const Grid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
  gap: theme.spacing(1.5),
}));

/** Inside the mission header: one row, scrolling sideways rather than
 *  wrapping when there are more vitals than fit. */
const Row = styled(Grid)(() => ({
  gridTemplateColumns: "none",
  gridAutoFlow: "column",
  gridAutoColumns: "minmax(120px, 1fr)",
  overflowX: "auto",
}));

const Label = styled(Typography)(({ theme }) => ({
  ...theme.typography.overline,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  display: "block",
  marginBottom: theme.spacing(0.75),
}));

/** One observation per card; the trend block is bottom-pinned so a row of
 *  cards keeps its baselines aligned however long a metric's name is. */
const Tile = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  minWidth: 0,
}));

const Metric = styled(Typography)(({ theme }) => ({
  ...theme.typography.overline,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  lineHeight: 1.5,
  minHeight: "3em",
  overflowWrap: "anywhere",
}));

const Reading = styled(Typography)(() => ({
  fontSize: "1.25rem",
  fontWeight: 600,
  lineHeight: 1.15,
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
}));

const Unit = styled("span")(({ theme }) => ({
  ...theme.typography.caption,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginLeft: theme.spacing(0.5),
}));

const Trend = styled(Box)(({ theme }) => ({
  marginTop: "auto",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
}));

const Change = styled(Typography)(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const Empty = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
}));

/**
 * The change line states arithmetic the reducer computed ("up from 78"),
 * never a characterisation of it. There is deliberately no colour coding by
 * value: that would be the interface making a clinical claim the product does
 * not make.
 */
function describeChange(row: ObservationRow): string {
  if (row.previous_value === null || row.previous_value === undefined) {
    return "first recorded reading";
  }
  if (row.direction === "unchanged") return `unchanged from ${row.previous_value}`;
  const word = row.direction === "higher" ? "up" : "down";
  return `${word} from ${row.previous_value}`;
}

type VitalsPanelProps = {
  latest: ObservationRow[];
  observations: ObservationRow[];
  title?: string;
  emptyText?: string;
  /** Inside another panel (the mission header): no card of its own, one row. */
  embedded?: boolean;
};

export function VitalsPanel(props: VitalsPanelProps) {
  const newest = props.latest.reduce<string | null>(
    (latest, row) => (latest === null || row.observed_at > latest ? row.observed_at : latest),
    null,
  );
  const updated = newest
    ? new Date(newest).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : null;
  const title = `${props.title ?? "Latest observations"}${updated ? ` · ${updated}` : ""}`;
  const Frame = props.embedded ? Box : Root;
  const Title = props.embedded ? Label : Heading;
  const Tiles = props.embedded ? Row : Grid;
  if (props.latest.length === 0) {
    return (
      <Frame>
        <Title>{title}</Title>
        <Empty>{props.emptyText ?? "No observations recorded yet."}</Empty>
      </Frame>
    );
  }
  return (
    <Frame>
      <Title>{title}</Title>
      <Tiles>
        {props.latest.map((row) => {
          const series = props.observations
            .filter((observation) => observation.metric === row.metric)
            .map((observation) => observation.value);
          const change = describeChange(row);
          return (
            <Tile key={row.metric}>
              <Metric>{row.metric.replace(/_/g, " ")}</Metric>
              <Reading>
                {row.value}
                <Unit>{row.unit}</Unit>
              </Reading>
              <Trend>
                <Sparkline values={series} />
                <Change title={change}>{change}</Change>
              </Trend>
            </Tile>
          );
        })}
      </Tiles>
    </Frame>
  );
}
